import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { TabProperties } from "../../types";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { SupplierFormTyping } from "./TabsController";
import { useGetModuleCurrenciesQuery } from "@redux/features/currencies/currenciesApi";
import { useGetModuleTaxesQuery } from "@redux/features/taxes/taxesApi";
import { useGetModulePaymentTermsQuery } from "@redux/features/paymentTerms/paymentTermsApi";
import { useGetModuleDeliveryTermsQuery } from "@redux/features/deliveryTerms/deliveryTermsApi";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";
import BaseButton from "@reusables/BaseButton";
import FormErrorWarning from "../modals/FormErrorWarning";
import { useGetLanguagesQuery } from "@redux/features/languages/languagesApi";
import { useAbility } from "@/casl.config";

export default function SettingsTab({onNext, onPrev}: TabProperties & {onPrev: () => void}) {
    const { t } = useTranslation("", { keyPrefix: "suppliers.general.tabs.settings" });

    const ability = useAbility();

    const {control, setValue} = useFormContext<SupplierFormTyping>();

    const {data: currencyOptions = [], isLoading: currencyOptionsLoading} = useGetModuleCurrenciesQuery("supplier");

    const {data: taxOptions = [], isLoading: taxOptionsLoading} = useGetModuleTaxesQuery("supplier");

    const {data: paymentTermsOptions = [], isLoading: paymentTermsOptionsLoading} = useGetModulePaymentTermsQuery("supplier");

    const {data: DeliveryTermsOptions = [], isLoading: DeliveryTermsOptionsLoading} = useGetModuleDeliveryTermsQuery("supplier");

    const {data: languageOptions = [], isLoading: isLanguageOptions} = useGetLanguagesQuery();

    const mode = useWatch({
        control,
        name: "mode"
    });

    useEffect(() => {
        if(currencyOptions && mode === "creation") {
            console.log(mode, currencyOptions)
            setValue("currency", currencyOptions.find(opt => opt.isBase));
        }
    }, [mode, currencyOptions]);

    return (
        <form className="space-y-[32px]" onSubmit={onNext}>
            <FormErrorWarning />

            <BaseInputsGrid>
                <Controller
                    control={control}
                    name='currency'
                    render={({field, fieldState}) => (
                        <BaseDropdown
                            {...field}
                            error={fieldState.error}

                            label={t("dropdowns.currency.label")}
                            placeholder={t("dropdowns.currency.placeholder")}

                            options={currencyOptions}
                            getter={{
                                key: opt => opt.id,
                                label: opt => opt.name,
                                caption: opt => opt.isBase ? `${t("dropdowns.currency.base").toUpperCase()}` : opt.code,
                            }}
                            isLoading={currencyOptionsLoading}

                            autocomplete
                        />
                    )}
                />

                <Controller
                    control={control}
                    name='tax_rate'
                    render={({field, fieldState}) => (
                        <BaseDropdown
                            {...field}
                            error={fieldState.error}

                            label={t("dropdowns.taxRate.label")}
                            placeholder={t("dropdowns.taxRate.placeholder")}

                            options={taxOptions}
                            getter={{
                                key: opt => opt.id,
                                label: opt => opt.rate.toString() + " %",
                                caption: opt => opt.name ?? ""
                            }}

                            isLoading={taxOptionsLoading}

                            autocomplete

                            disabled={mode === "editing" && ability.cannot("tax_rate.edit", "supplier")}
                        />
                    )}
                />

                <Controller
                    control={control}
                    name='payment_terms'
                    render={({field, fieldState}) => (
                        <BaseDropdown
                            {...field}
                            error={fieldState.error}

                            label={t("dropdowns.paymentTerms.label")}
                            placeholder={t("dropdowns.paymentTerms.placeholder")}

                            options={paymentTermsOptions}
                            getter={{
                                key: opt => opt.id,
                                label: opt => opt.name,
                            }}
                            isLoading={paymentTermsOptionsLoading}
                        />
                    )}
                />

                <Controller
                    control={control}
                    name='delivery_terms'
                    render={({field, fieldState}) => (
                        <BaseDropdown
                            {...field}
                            error={fieldState.error}

                            label={t("dropdowns.deliveryTerms.label")}
                            placeholder={t("dropdowns.deliveryTerms.placeholder")}

                            options={DeliveryTermsOptions}
                            getter={{
                                key: opt => opt.id,
                                label: opt => opt.name,
                            }}
                            isLoading={DeliveryTermsOptionsLoading}
                        />
                    )}
                />

                <Controller
                    control={control}
                    name="language"
                    render={({field, fieldState}) => (
                        <BaseDropdown
                            {...field}
                            error={fieldState.error}

                            options={languageOptions}
                            getter={{
                                key: opt => opt.id,
                                label: opt => opt.name
                            }}
                            
                            label={t("dropdowns.language.label")}
                            placeholder={t("dropdowns.language.placeholder")}
                            isLoading={isLanguageOptions}
                            autocomplete
                        />
                    )}
                />
            </BaseInputsGrid>
            <div className="flex justify-center space-x-[24px]" id={"navigation"}>
                <BaseButton
                    text={t("buttons.prev")}
                    size="md"
                    buttonWidth="200px"
                    primaryOutlined
                    type={"button"}

                    onClick={onPrev}
                />

                {mode === "creation" && (
                    <BaseButton
                        text={t("buttons.save")}
                        size="md"
                        buttonWidth="200px"
                        onClick={onNext}
                    />
                )}
            </div>
        </form>
    )
}
